import { ChevronDown, ChevronsUpDown, ChevronUp, Search, Upload } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/ui/button';
import { Card, CardContent } from '~/components/ui/card';
import { Input } from '~/components/ui/input';
import { Skeleton } from '~/components/ui/skeleton';
import { cn } from '~/utils/cn';
import { downloadCsv } from '~/utils/csv';

import type { CategoryPerformanceRow, RevenueSource } from '../../types/experiment.types';
import { formatPercentFromPercent } from '../../utils/format-percent';

import { RevenueSourceTag } from './revenue-source-tag';

type SortColumn =
  | 'category'
  | 'revContribution'
  | 'didImpression'
  | 'didClicks'
  | 'didConversions'
  | 'didConvValue';

type SortDirection = 'asc' | 'desc';

interface SortState {
  column: SortColumn;
  direction: SortDirection;
}

interface Props {
  rows: CategoryPerformanceRow[];
  isLoading?: boolean | undefined;
  /** When true the body is replaced with an error card. The header stays visible so the page layout doesn't jump. */
  isError?: boolean | undefined;
  /** Source of the revenue figures; null/absent renders no tag. */
  revenueSource?: RevenueSource | null | undefined;
}

const COLUMNS: { key: SortColumn; labelKey: string; align: 'left' | 'right' }[] = [
  { key: 'category', labelKey: 'detail.categoryPerformance.columns.category', align: 'left' },
  {
    key: 'revContribution',
    labelKey: 'detail.categoryPerformance.columns.revContribution',
    align: 'left',
  },
  {
    key: 'didImpression',
    labelKey: 'detail.categoryPerformance.columns.didImpression',
    align: 'left',
  },
  { key: 'didClicks', labelKey: 'detail.categoryPerformance.columns.didClicks', align: 'left' },
  {
    key: 'didConversions',
    labelKey: 'detail.categoryPerformance.columns.didConversions',
    align: 'left',
  },
  {
    key: 'didConvValue',
    labelKey: 'detail.categoryPerformance.columns.didConvValue',
    align: 'left',
  },
];

const COLUMN_DEFAULTS: Record<SortColumn, string> = {
  category: 'Category',
  revContribution: 'Rev. Contribc.',
  didImpression: 'DID Impression',
  didClicks: 'DID Clicks',
  didConversions: 'DID Conversions',
  didConvValue: 'DID Conv. Value',
};

/**
 * "Category Performance — Revenue Weighted" table on the right side of the
 * category-mode Performance tab. Sortable by any column; case-insensitive
 * filter on the category name. Negative DID cells use the error tint, positive
 * cells use the success tint — matches the screenshot's color coding.
 *
 * Future enhancement (pending PM review) — Trend ⇄ Table toggle that adds an
 * ECharts heatmap view alongside the table. See
 * `docs/experiment-details-chatbot.md` §8.1 for spec + open questions.
 */
export function ExperimentCategoryPerformanceTable({
  rows,
  isLoading,
  isError,
  revenueSource,
}: Props) {
  const { t } = useTranslation('experiments');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortState>({ column: 'revContribution', direction: 'desc' });
  const isEmpty = !isLoading && !isError && rows.length === 0;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => r.category.toLowerCase().includes(q));
  }, [rows, search]);

  const sorted = useMemo(() => {
    const copy = [...filtered];
    copy.sort((a, b) => compareRows(a, b, sort));
    return copy;
  }, [filtered, sort]);

  const handleSort = (column: SortColumn) => {
    setSort((prev) => {
      if (prev.column !== column) return { column, direction: 'desc' };
      return { column, direction: prev.direction === 'desc' ? 'asc' : 'desc' };
    });
  };

  // Exports the currently filtered/sorted rows (not the raw prop data) so the CSV matches what's on screen.
  const handleExport = () => {
    const csvRows = sorted.map((row) => ({
      category: row.category,
      revContribution: `${row.revContributionPct.toFixed(1)}%`,
      didImpression: formatPercentFromPercent(row.didImpressionPct, { precision: 'chart-1dp' }),
      didClicks: formatPercentFromPercent(row.didClicksPct, { precision: 'chart-1dp' }),
      didConversions: formatPercentFromPercent(row.didConversionsPct, { precision: 'chart-1dp' }),
      didConvValue: formatPercentFromPercent(row.didConvValuePct, { precision: 'chart-1dp' }),
    }));
    downloadCsv(
      'category-performance-revenue-weighted',
      csvRows,
      COLUMNS.map((col) => ({ key: col.key, label: t(col.labelKey, COLUMN_DEFAULTS[col.key]) })),
    );
  };

  const maxRevContribution = Math.max(1, ...rows.map((r) => r.revContributionPct));

  return (
    <Card className="min-w-0 overflow-hidden">
      <CardContent className="min-w-0 space-y-4 p-6">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex min-w-0 flex-1 flex-col">
            <h3 className="flex flex-wrap items-center gap-2 text-[16px] font-medium wrap-break-word">
              {t('detail.categoryPerformance.title', 'Category Performance — Revenue Weighted')}
              <RevenueSourceTag source={revenueSource} />
            </h3>
            <p className="text-muted-foreground text-sm wrap-break-word">
              {t(
                'detail.categoryPerformance.subtitle',
                'Impressions column highlighted by intensity · Sorted by revenue contribution',
              )}
            </p>
          </div>
          <div className="flex shrink-0 flex-row items-center gap-2">
            <div className="relative">
              <Search
                className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2"
                aria-hidden="true"
              />
              <Input
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                }}
                placeholder={t('detail.categoryPerformance.search', 'Search by category')}
                disabled={isEmpty}
                className={cn('w-[220px] pl-8', isEmpty && 'cursor-not-allowed')}
                aria-label={t('detail.categoryPerformance.search', 'Search by category')}
              />
            </div>
            <Button
              variant="outline"
              onClick={handleExport}
              disabled={isEmpty}
              className={cn('gap-2', isEmpty ? 'cursor-not-allowed' : 'cursor-pointer')}
            >
              <Upload className="h-4 w-4" aria-hidden="true" />
              {t('detail.categoryPerformance.exportCsv', 'Export CSV')}
            </Button>
          </div>
        </div>

        {/* Error / empty / table — body slot */}
        {isError ? (
          <div className="border-error/30 bg-error-50 space-y-1 rounded-md border p-4">
            <p className="text-foreground text-sm font-medium">
              {t('detail.categoryPerformance.errorTitle', 'Couldn’t load category data.')}
            </p>
            <p className="text-muted-foreground text-xs leading-5">
              {t('detail.categoryPerformance.errorBody', 'Refresh the page to try again.')}
            </p>
          </div>
        ) : !isLoading && rows.length === 0 ? (
          <div className="bg-muted/30 rounded-md border border-dashed p-8 text-center">
            <p className="text-muted-foreground text-sm">
              {t('detail.categoryPerformance.empty', 'No category data yet for this experiment.')}
            </p>
          </div>
        ) : (
          <div className="max-h-100 overflow-auto rounded-md border">
            <table className="w-full min-w-180 text-sm">
              <thead className="bg-background sticky top-0 z-10">
                <tr>
                  {COLUMNS.map((col) => (
                    <th key={col.key} className="text-muted-foreground px-4 py-3 font-medium">
                      <button
                        type="button"
                        onClick={() => {
                          handleSort(col.key);
                        }}
                        className={cn(
                          'hover:text-foreground inline-flex cursor-pointer items-center gap-1.5 transition-colors',
                          col.align === 'right' && 'justify-end',
                        )}
                        aria-label={`Sort by ${t(col.labelKey, COLUMN_DEFAULTS[col.key])}`}
                      >
                        <span>{t(col.labelKey, COLUMN_DEFAULTS[col.key])}</span>
                        <SortIcon active={sort.column === col.key} direction={sort.direction} />
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {isLoading
                  ? Array.from({ length: 6 }).map((_, i) => (
                      <tr key={i} className="border-t">
                        {COLUMNS.map((col) => (
                          <td key={col.key} className="px-4 py-3">
                            <Skeleton className="h-4 w-20" />
                          </td>
                        ))}
                      </tr>
                    ))
                  : sorted.map((row) => (
                      <tr key={row.category} className="border-t">
                        <td className="text-foreground px-4 py-3 font-medium">{row.category}</td>
                        <td className="px-4 py-3">
                          <IntensityBar value={row.revContributionPct} max={maxRevContribution} />
                        </td>
                        <td className={cn('px-4 py-3', signTintClass(row.didImpressionPct))}>
                          {formatPercentFromPercent(row.didImpressionPct, {
                            precision: 'chart-1dp',
                          })}
                        </td>
                        <td className={cn('px-4 py-3', signTintClass(row.didClicksPct))}>
                          {formatPercentFromPercent(row.didClicksPct, { precision: 'chart-1dp' })}
                        </td>
                        <td className={cn('px-4 py-3', signTintClass(row.didConversionsPct))}>
                          {formatPercentFromPercent(row.didConversionsPct, {
                            precision: 'chart-1dp',
                          })}
                        </td>
                        <td className={cn('px-4 py-3', signTintClass(row.didConvValuePct))}>
                          {formatPercentFromPercent(row.didConvValuePct, {
                            precision: 'chart-1dp',
                          })}
                        </td>
                      </tr>
                    ))}
              </tbody>
            </table>
          </div>
        )}

        <p className="text-muted-foreground text-xs">
          {t(
            'detail.categoryPerformance.footer',
            'Some daily noise is normal — look for sustained patterns rather than individual spikes or dips. Switch to table view for all metrics side by side.',
          )}
        </p>
      </CardContent>
    </Card>
  );
}

interface IntensityBarProps {
  value: number;
  max: number;
}

/** Inline intensity bar paired with the percent — visual emphasis for revenue-weighted columns. */
function IntensityBar({ value, max }: IntensityBarProps) {
  const widthPct = Math.min(100, (value / max) * 100);
  return (
    <div className="flex items-center gap-3">
      <div className="bg-muted h-1.5 w-16 overflow-hidden rounded-full">
        <div className="bg-primary h-full" style={{ width: `${widthPct.toFixed(1)}%` }} />
      </div>
      <span className="text-foreground tabular-nums">{value.toFixed(1)}%</span>
    </div>
  );
}

interface SortIconProps {
  active: boolean;
  direction: SortDirection;
}

function SortIcon({ active, direction }: SortIconProps) {
  if (!active) return <ChevronsUpDown className="h-3.5 w-3.5" aria-hidden="true" />;
  return direction === 'desc' ? (
    <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
  ) : (
    <ChevronUp className="h-3.5 w-3.5" aria-hidden="true" />
  );
}

function pickValue(row: CategoryPerformanceRow, column: SortColumn): number | string {
  switch (column) {
    case 'category':
      return row.category;
    case 'revContribution':
      return row.revContributionPct;
    case 'didImpression':
      return row.didImpressionPct;
    case 'didClicks':
      return row.didClicksPct;
    case 'didConversions':
      return row.didConversionsPct;
    case 'didConvValue':
      return row.didConvValuePct;
  }
}

function compareRows(
  a: CategoryPerformanceRow,
  b: CategoryPerformanceRow,
  sort: SortState,
): number {
  const aVal = pickValue(a, sort.column);
  const bVal = pickValue(b, sort.column);
  const sign = sort.direction === 'asc' ? 1 : -1;
  if (typeof aVal === 'number' && typeof bVal === 'number') {
    return (aVal - bVal) * sign;
  }
  return String(aVal).localeCompare(String(bVal)) * sign;
}

function signTintClass(value: number): string {
  if (value > 0) return 'bg-success-50 text-success font-medium';
  if (value < 0) return 'bg-error-50 text-error font-medium';
  return '';
}
